import React, { useCallback } from 'react';
import {
  Paper,
  Box,
  Chip,
  Tooltip,
  IconButton,
  Grid,
  TextField,
  Stack,
  FormControlLabel,
  Switch,
  InputAdornment
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import HelpIcon from '../../help/HelpIcon';

const HELP_TOPIC = { module: 'modelo-semantico', topic: 'tabla-semantica' };

const outlinedInputSx = {
  color: 'var(--wb-text)',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--wb-blue-strong)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--wb-blue)' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--wb-blue-light)' }
};

const ColumnCard = ({
  column,
  index,
  error,
  labelColor,
  switchSx,
  onFieldChange,
  onSwitchChange,
  onRemove
}) => {
  const primaryLabel = column.is_primary_key ? 'Es llave primaria' : 'No es llave primaria';
  const foreignLabel = column.is_foreign_key ? 'Es llave foránea' : 'No es llave foránea';
  const nullableLabel = column.nullable ? 'Permite nulos' : 'No permite nulos';
  const requiredLabel = column.required ? 'Es requerido' : 'No es requerido';
  const sensitiveLabel = column.sensitive ? 'Es dato sensible' : 'No es dato sensible';

  const handleFieldChange = useCallback(
    (field) => (event) => {
      onFieldChange(index, field, event.target.value);
    },
    [index, onFieldChange]
  );

  const handleSwitchToggle = useCallback(
    (field) => (_, checked) => {
      if (field === 'nullable' && checked && (column.is_primary_key || column.is_foreign_key)) {
        return;
      }
      if (field === 'required' && !checked && (column.is_primary_key || column.is_foreign_key)) {
        return;
      }

      onSwitchChange(index, field, checked);

      if (field === 'is_primary_key') {
        if (checked) {
          onSwitchChange(index, 'is_foreign_key', false);
          onSwitchChange(index, 'nullable', false);
          onSwitchChange(index, 'required', true);
        }
      } else if (field === 'is_foreign_key') {
        if (checked) {
          onSwitchChange(index, 'is_primary_key', false);
          onSwitchChange(index, 'nullable', false);
          onSwitchChange(index, 'required', true);
        }
      }
    },
    [index, onSwitchChange, column]
  );

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: 'var(--wb-x-bg-1c2436, #1c2436)',
        border: '1px solid var(--wb-x-border-3b82f659, rgba(59, 130, 246, 0.35))',
        borderRadius: 0,
        p: 3,
        position: 'relative'
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          display: 'flex',
          gap: 1
        }}
      >
        <Chip
          label={'Columna ' + (index + 1)}
          size="small"
          sx={{ bgcolor: 'var(--wb-accent-18)', color: 'var(--wb-text-label)' }}
        />
        <Tooltip title="Eliminar columna">
          <span>
            <IconButton
              onClick={() => onRemove(index)}
              size="small"
              sx={{ color: 'var(--wb-accent)' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Nombre de columna *"
            value={column.column_name}
            onChange={handleFieldChange('column_name')}
            fullWidth
            error={Boolean(error)}
            helperText={error || 'Nombre físico en el origen'}
            InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
            InputProps={{ sx: { ...outlinedInputSx, '& .MuiOutlinedInput-input': { py: '6px', px: '12px' } }, endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="nombre-de-columna" /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Nombre de negocio"
            value={column.business_name}
            onChange={handleFieldChange('business_name')}
            fullWidth
            InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
            InputProps={{ sx: { ...outlinedInputSx, '& .MuiOutlinedInput-input': { py: '6px', px: '12px' } }, endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="nombre-de-negocio" /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Tipo de dato"
            value={column.data_type}
            onChange={handleFieldChange('data_type')}
            fullWidth
            InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
            InputProps={{ sx: { ...outlinedInputSx, '& .MuiOutlinedInput-input': { py: '6px', px: '12px' } }, endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="tipo-de-dato" /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Rol semántico"
            value={column.semantic_role}
            onChange={handleFieldChange('semantic_role')}
            fullWidth
            InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
            InputProps={{ sx: { ...outlinedInputSx, '& .MuiOutlinedInput-input': { py: '6px', px: '12px' } }, endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="rol-semántico" /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Tipo semántico"
            value={column.semantic_type}
            onChange={handleFieldChange('semantic_type')}
            fullWidth
            InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
            InputProps={{ sx: { ...outlinedInputSx, '& .MuiOutlinedInput-input': { py: '6px', px: '12px' } }, endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="tipo-semántico" /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Referencia (FK)"
            value={column.references}
            onChange={handleFieldChange('references')}
            fullWidth
            InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
            InputProps={{ sx: { ...outlinedInputSx, '& .MuiOutlinedInput-input': { py: '6px', px: '12px' } }, endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="referencias" /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Descripción"
            value={column.description}
            onChange={handleFieldChange('description')}
            fullWidth
            multiline
            minRows={2}
            InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
            InputProps={{ sx: { ...outlinedInputSx, '& .MuiOutlinedInput-input': { py: '6px', px: '12px' } }, endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="descripción-1" /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Patrón"
            value={column.pattern}
            onChange={handleFieldChange('pattern')}
            fullWidth
            InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
            InputProps={{ sx: { ...outlinedInputSx, '& .MuiOutlinedInput-input': { py: '6px', px: '12px' } }, endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="patrón--unidad--agregación--comportamiento-temporal--granularidad-temporal--dominio" /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Unidad"
            value={column.unit}
            onChange={handleFieldChange('unit')}
            fullWidth
            InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
            InputProps={{ sx: { ...outlinedInputSx, '& .MuiOutlinedInput-input': { py: '6px', px: '12px' } }, endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="patrón--unidad--agregación--comportamiento-temporal--granularidad-temporal--dominio" /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Agregación"
            value={column.aggregation}
            onChange={handleFieldChange('aggregation')}
            fullWidth
            InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
            InputProps={{ sx: { ...outlinedInputSx, '& .MuiOutlinedInput-input': { py: '6px', px: '12px' } }, endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="patrón--unidad--agregación--comportamiento-temporal--granularidad-temporal--dominio" /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Comportamiento temporal"
            value={column.temporal_behavior}
            onChange={handleFieldChange('temporal_behavior')}
            fullWidth
            InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
            InputProps={{ sx: { ...outlinedInputSx, '& .MuiOutlinedInput-input': { py: '6px', px: '12px' } }, endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="patrón--unidad--agregación--comportamiento-temporal--granularidad-temporal--dominio" /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Granularidad temporal"
            value={column.time_grain}
            onChange={handleFieldChange('time_grain')}
            fullWidth
            InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
            InputProps={{ sx: { ...outlinedInputSx, '& .MuiOutlinedInput-input': { py: '6px', px: '12px' } }, endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="patrón--unidad--agregación--comportamiento-temporal--granularidad-temporal--dominio" /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Dominio"
            value={column.domain}
            onChange={handleFieldChange('domain')}
            fullWidth
            InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
            InputProps={{ sx: { ...outlinedInputSx, '& .MuiOutlinedInput-input': { py: '6px', px: '12px' } }, endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="patrón--unidad--agregación--comportamiento-temporal--granularidad-temporal--dominio" /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControlLabel
              control={
                <Switch
                  checked={column.is_primary_key}
                  onChange={handleSwitchToggle('is_primary_key')}
                  sx={switchSx}
                />
              }
              label={primaryLabel}
              sx={{ color: 'var(--wb-text-strong)' }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={column.is_foreign_key}
                  onChange={handleSwitchToggle('is_foreign_key')}
                  sx={switchSx}
                />
              }
              label={foreignLabel}
              sx={{ color: 'var(--wb-text-strong)' }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={column.nullable}
                  onChange={handleSwitchToggle('nullable')}
                  sx={switchSx}
                />
              }
              label={nullableLabel}
              sx={{ color: 'var(--wb-text-strong)' }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={column.required}
                  onChange={handleSwitchToggle('required')}
                  sx={switchSx}
                />
              }
              label={requiredLabel}
              sx={{ color: 'var(--wb-text-strong)' }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={column.sensitive}
                  onChange={handleSwitchToggle('sensitive')}
                  sx={switchSx}
                />
              }
              label={sensitiveLabel}
              sx={{ color: 'var(--wb-text-strong)' }}
            />
            <HelpIcon {...HELP_TOPIC} anchor="es-clave-primaria--es-clave-foránea--permite-nulos--requerido--sensible" />
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Sinónimos"
            value={column.synonymsText}
            onChange={handleFieldChange('synonymsText')}
            fullWidth
            multiline
            minRows={2}
            helperText="Separar por comas"
            InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
            InputProps={{ sx: { ...outlinedInputSx, '& .MuiOutlinedInput-input': { py: '6px', px: '12px' } }, endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="sinónimos--valores-de-ejemplo--notas-de-uso--restricciones-de-uso" /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Ejemplos"
            value={column.exampleValuesText}
            onChange={handleFieldChange('exampleValuesText')}
            fullWidth
            multiline
            minRows={2}
            helperText="Separar por comas"
            InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
            InputProps={{ sx: { ...outlinedInputSx, '& .MuiOutlinedInput-input': { py: '6px', px: '12px' } }, endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="sinónimos--valores-de-ejemplo--notas-de-uso--restricciones-de-uso" /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Notas de uso"
            value={column.usageNotesText}
            onChange={handleFieldChange('usageNotesText')}
            fullWidth
            multiline
            minRows={2}
            helperText="Separar por comas"
            InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
            InputProps={{ sx: { ...outlinedInputSx, '& .MuiOutlinedInput-input': { py: '6px', px: '12px' } }, endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="sinónimos--valores-de-ejemplo--notas-de-uso--restricciones-de-uso" /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Restricciones de uso"
            value={column.usageConstraintsText}
            onChange={handleFieldChange('usageConstraintsText')}
            fullWidth
            multiline
            minRows={2}
            helperText="Separar por comas"
            InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
            InputProps={{ sx: { ...outlinedInputSx, '& .MuiOutlinedInput-input': { py: '6px', px: '12px' } }, endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="sinónimos--valores-de-ejemplo--notas-de-uso--restricciones-de-uso" /></InputAdornment> }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ColumnCard;
